import React, { useCallback, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TitleWithDivider from "components/Common/components/TitleWithDivider";
import locationIcon from "assets/images/svg/location-icon.svg";
import CustomButton from "components/Common/buttons/CustomButton";
import NGIDropdownButton from "components/Common/buttons/NGIDropdownButton";
import menuIcon from "assets/images/svg/MenuVertical.svg";
import settings from "assets/images/svg/settings.svg";
import toggle from "assets/images/svg/toggle.svg";
import removeIcon from "assets/images/svg/Delete.svg";
import { USER_ROLES } from "utils/constants";
import AddMiniDepotModal from "pages/depot-management/depotDetails/Tabs/AddMiniDepotModal";
import AssignMDSCModal from "pages/depot-management/depotDetails/Tabs/AssignMDSCModal";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { deleteMiniDepot, retrieveAllAssets, updateMiniDepot } from "utils/api";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import classnames from "classnames";
import { userHasOneOfRoles } from "utils/helpers/functions";

const useStyles = makeStyles({
  depotList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 15,
  },
  miniDepotItem: {
    display: "flex",
    flexDirection: "row",
    border: "1px solid #0000001A",
    borderRadius: 5,
  },
  infoContainer: {
    display: "flex",
    width: "80%",
    padding: 20,
  },
  name: {
    fontWeight: 600,
    flex: 1,
  },
  assignedToContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20%",
    border: "1px solid #0000001A",
    borderTop: "none",
    borderBottom: "none",
    padding: "0 20px",
  },
  assignedToName: {
    fontWeight: 600,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    textDecoration: "underline",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  location: {
    display: "flex",
    alignItems: "center",
    fontWeight: 500,

    "&>img": {
      marginRight: 5,
    },
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  assignBtn: {
    fontWeight: 500,
    border: "1px dashed #0000004D",
    backgroundColor: "#f0f0f0",
    padding: "5px 20px",
    borderRadius: 3,
    textAlign: "center",
    width: "100%",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  noDepots: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  Active: {
    color: "#17C347",
  },
  Inactive: {
    color: "#FF2E00",
  },
  imgBtn: {
    height: 20,
    cursor: "pointer",
  },
  disabled: {
    pointerEvents: "none",
    opacity: 0.6,
  },
});

const MiniDepots = ({ miniDepots, mainDepotId, mainDepotName, updateData, setLoader, isLoading }) => {
  const classes = useStyles();
  const [activeMiniDepot, setActiveMiniDepot] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [isConfirmationRemoveOpen, setIsConfirmationRemoveOpen] = useState(false);
  const [isConfirmationToggleOpen, setIsConfirmationToggleOpen] = useState(false);

  const openAssignModal = useCallback(
    e => {
      const selectedDepot = miniDepots.find(item => item.id === e.currentTarget.dataset.id);
      setActiveMiniDepot(selectedDepot);
      setShowAssignModal(true);
    },
    [miniDepots]
  );

  const closeAssignModal = useCallback(() => {
    setActiveMiniDepot(null);
    setShowAssignModal(false);
  }, []);

  const openAddModal = useCallback(() => setShowAddModal(true), []);
  const closeAddModal = useCallback(() => {
    setActiveMiniDepot(null);
    setShowAddModal(false);
  }, []);

  const openConfirmationRemoveModal = useCallback(depot => {
    setActiveMiniDepot(depot);
    setIsConfirmationRemoveOpen(true);
  }, []);
  const closeConfirmationRemoveModal = useCallback(() => {
    setActiveMiniDepot(null);
    setIsConfirmationRemoveOpen(false);
  }, []);

  const handleRemove = useCallback(() => {
    setLoader(true);
    deleteMiniDepot(activeMiniDepot.id)
      .then(() => {
        setLoader(false);
        successMessage("Mini Depot was removed successfully");
        closeConfirmationRemoveModal();
        updateData();
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, [activeMiniDepot]);

  const openConfirmationToggleModal = useCallback(depot => {
    setActiveMiniDepot(depot);
    setIsConfirmationToggleOpen(true);
  }, []);
  const closeConfirmationToggleModal = useCallback(() => {
    setActiveMiniDepot(null);
    setIsConfirmationToggleOpen(false);
  }, []);

  const handleToggleDepot = useCallback(async () => {
    setLoader(true);
    if (activeMiniDepot.status === "Active") {
      const {
        data: { result },
      } = await retrieveAllAssets({
        custodian: activeMiniDepot.name,
        custodianType: "depot",
      });
      if (result.items.length) {
        warnMessage(`Depot has ${result.items.length} assigned assets. It can't be deactivated.`, 5000);
        setLoader(false);
        return;
      }
    }
    updateMiniDepot(
      {
        status: activeMiniDepot.status === "Active" ? "Inactive" : "Active",
      },
      activeMiniDepot?.id
    )
      .then(() => {
        setLoader(false);
        successMessage(
          `Mini Depot was ${activeMiniDepot.status === "Active" ? "Deactivated" : "Activated"} successfully`
        );
        closeConfirmationRemoveModal();
        updateData();
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, [activeMiniDepot]);

  return (
    <>
      <TitleWithDivider
        title="Mini Depots"
        button={
          <CustomButton
            roles={[USER_ROLES.IT_OPERATIONS_LEADERSHIP, USER_ROLES.IT_OPERATIONS]}
            onClick={openAddModal}
            icon={<i className="bx bx-plus-circle" />}
            type="primary"
            size="small"
            text="Mini Depot"
          />
        }
      />
      {miniDepots?.length ? (
        <div className={classes.depotList}>
          {miniDepots?.map(item => (
            <div key={item.id} className={classes.miniDepotItem}>
              <div className={classes.infoContainer}>
                <div className={classes.name}>
                  {item.name} (<span className={classes[item.status]}>{item.status}</span>)
                </div>
                <div className={classes.location}>
                  <img src={locationIcon} alt="member" />
                  {item.address}
                </div>
              </div>

              <div className={classes.assignedToContainer}>
                {item?.assignedTo ? (
                  <div
                    className={classnames(
                      classes.assignedToName,
                      userHasOneOfRoles([USER_ROLES.MDSC]) && classes.disabled
                    )}
                    onClick={openAssignModal}
                    data-id={item.id}
                  >
                    {item.assignedTo}
                  </div>
                ) : (
                  <div
                    className={classnames(
                      classes.assignBtn,
                      (userHasOneOfRoles([USER_ROLES.MDSC]) || item.status === "Inactive") && classes.disabled
                    )}
                    onClick={openAssignModal}
                    data-id={item.id}
                  >
                    Assign MDSC
                  </div>
                )}
              </div>
              <div className={classes.actionsContainer}>
                <NGIDropdownButton
                  position="left"
                  toggleEl={<img className={classes.imgBtn} src={menuIcon} alt="action-icon" />}
                  menuItems={[
                    {
                      label: "Edit Mini Depot",
                      subLabel: "Change name and address",
                      icon: settings,
                      disabled: item.status === "Inactive",
                      callback: () => {
                        setActiveMiniDepot(item);
                        setShowAddModal(true);
                      },
                      roles: [
                        USER_ROLES.SYSTEM_FULL_ADMINS,
                        USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                        USER_ROLES.IT_OPERATIONS,
                        USER_ROLES.DEPOT_CLERK,
                        USER_ROLES.MWT,
                      ],
                    },
                    {
                      label: `${item.status === "Active" ? "Deactivate" : "Activate"} Depot`,
                      subLabel: "Change the operating status",
                      icon: toggle,
                      callback: () => openConfirmationToggleModal(item),
                      roles: [
                        USER_ROLES.SYSTEM_FULL_ADMINS,
                        USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                        USER_ROLES.IT_OPERATIONS,
                        USER_ROLES.DEPOT_CLERK,
                        USER_ROLES.MWT,
                      ],
                    },
                    {
                      label: "Delete Mini Depot",
                      subLabel: "Remove from mini depots",
                      icon: removeIcon,
                      disabled: item.status === "Active",
                      callback: () => openConfirmationRemoveModal(item),
                      textColor: "#FF2E00",
                      roles: [
                        USER_ROLES.SYSTEM_FULL_ADMINS,
                        USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                        USER_ROLES.IT_OPERATIONS,
                        USER_ROLES.DEPOT_CLERK,
                        USER_ROLES.MWT,
                      ],
                    },
                  ]}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={classes.noDepots}>No Mini Depots</div>
      )}

      {showAddModal && (
        <AddMiniDepotModal
          mainDepotName={mainDepotName}
          activeItem={activeMiniDepot}
          mainId={mainDepotId}
          onClose={closeAddModal}
          update={updateData}
        />
      )}
      {showAssignModal && (
        <AssignMDSCModal
          mainDepotName={mainDepotName}
          activeItem={activeMiniDepot}
          mainId={mainDepotId}
          onClose={closeAssignModal}
          update={updateData}
        />
      )}
      {isConfirmationRemoveOpen && (
        <ConfirmationModal
          loading={isLoading}
          description="Are you sure you want to delete a mini depot?"
          onCancel={closeConfirmationRemoveModal}
          onConfirm={handleRemove}
        />
      )}
      {isConfirmationToggleOpen && (
        <ConfirmationModal
          loading={isLoading}
          description={
            <span>
              Are you sure you want to <b>{activeMiniDepot.status === "Active" ? "Deactivate" : "Activate"}</b> a mini
              depot?
            </span>
          }
          onCancel={closeConfirmationToggleModal}
          onConfirm={handleToggleDepot}
        />
      )}
    </>
  );
};

export default MiniDepots;
